import { Activity, AlertTriangle, Users, Timer } from 'lucide-react';
import { Card } from '@/components/ui/card';
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from 'recharts';
import { useInternalTraffic } from '@/hooks/useInternalPanel';
import { InternalTable, EmptyState, LoaderBlock, StatCard, fmtHour, fmtMs, fmtNum, fmtDateTime } from './shared';
import { useTranslation } from 'react-i18next';

export function TrafficTab() {
  const { t } = useTranslation();
  const { data, isLoading } = useInternalTraffic();

  if (isLoading) return <LoaderBlock />;
  if (!data) return <EmptyState title={t('internal.traffictab.sin_datos_de_trafico')} />;

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        {t('internal.traffictab.trafico_de_hoy')}{data.day}{t('internal.traffictab.la_serie_horaria_sale_de_cloudwatch')}
      </p>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard icon={Activity} label={t('internal.traffictab.llamadas_hoy')} value={fmtNum(data.totals.calls)} sub={t('internal.traffictab.n_page_views_fondo', { n: fmtNum(data.totals.pageViews), bg: fmtNum(data.totals.backgroundCalls) })} tone="blue" />
        <StatCard icon={Users} label={t('internal.traffictab.usuarios_hoy')} value={fmtNum(data.totals.uniqueUsers)} tone="purple" />
        <StatCard icon={AlertTriangle} label={t('internal.traffictab.errores_hoy')} value={fmtNum(data.totals.errors)} tone={data.totals.errors > 0 ? 'rose' : 'green'} />
        <StatCard icon={Timer} label={t('internal.traffictab.latencia_p95')} value={fmtMs(data.totals.p95Ms)} sub={`p50 ${fmtMs(data.totals.p50Ms)}`} tone="amber" />
      </div>

      <Card className="p-5">
        <h3 className="text-sm font-semibold mb-3">{t('internal.traffictab.ultimas_24_horas_cloudwatch_lambda_de')}</h3>
        {data.hourly.length === 0 ? (
          <p className="text-sm text-muted-foreground py-4 text-center">{t('internal.traffictab.sin_datos_de_cloudwatch_todavia')}</p>
        ) : (
          <ResponsiveContainer width="100%" height={240}>
            <LineChart data={data.hourly}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="hour" tickFormatter={fmtHour} fontSize={11} />
              <YAxis allowDecimals={false} fontSize={11} width={40} />
              <Tooltip labelFormatter={(v) => fmtHour(String(v))} />
              <Legend />
              <Line type="monotone" dataKey="invocations" name="Requests" stroke="#3b82f6" dot={false} strokeWidth={2} />
              <Line type="monotone" dataKey="errors" name="Errores" stroke="#ef4444" dot={false} strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        )}
      </Card>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card className="p-5">
          <h3 className="text-sm font-semibold mb-3">{t('internal.traffictab.por_modulo_hoy')}</h3>
          {data.byModule.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4 text-center">{t('internal.traffictab.sin_llamadas_hoy_todavia')}</p>
          ) : (
            <InternalTable
              rows={data.byModule}
              rowKey={(m) => m.module}
              columns={[
                { key: 'module', label: t('internal.traffictab.modulo'), render: (m) => m.module },
                { key: 'calls', label: t('internal.traffictab.llamadas'), align: 'right', render: (m) => fmtNum(m.calls) },
                {
                  key: 'errors', label: t('internal.traffictab.errores'), align: 'right',
                  render: (m) => <span className={m.errors > 0 ? 'text-rose-600 dark:text-rose-400 font-medium' : ''}>{fmtNum(m.errors)}</span>,
                },
              ]}
            />
          )}
        </Card>
        <Card className="p-5">
          <h3 className="text-sm font-semibold mb-3">{t('internal.traffictab.errores_recientes_hoy')}</h3>
          {data.recentErrors.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4 text-center">{t('internal.traffictab.sin_errores_hoy')}</p>
          ) : (
            <div className="space-y-2">
              {data.recentErrors.map((e, i) => (
                <div key={i} className="text-xs border-b last:border-0 pb-2">
                  <div className="flex justify-between gap-2">
                    <span className="font-mono font-medium truncate">{e.path}</span>
                    <span className="text-rose-600 dark:text-rose-400 shrink-0">{e.errorCode ?? 'ERROR'}</span>
                  </div>
                  <div className="text-muted-foreground mt-0.5">
                    {e.userEmail} · {fmtDateTime(e.timestamp)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
